import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, ReactElement, useEffect, useMemo, useState } from "react";
import { Alert, Col, Container, Nav, Row } from "react-bootstrap";
import useEUFinanceCurrentUser from "../../modules/filestore/hooks/useEUFinanceCurrentUser";
import "./styles.scss";

// https://developer.salesforce.com/docs/platform/lwc/guide/lightning-out-markup.html
type HasLightning = {
  $Lightning: {
    use: (
      applicationTag: string,
      callback: () => void,
      lightningEndpointURI: string,
      authToken?: string,
      paramsObject?: unknown
    ) => void;
    createComponent: (
      type: string,
      attributes: Record<string, unknown>,
      locator: string,
      callback: (
        component: unknown,
        status?: unknown,
        statusMessage?: string
      ) => void
    ) => void;
  };
};

const LWC_APPLICATION_TAG = "c:LightningOutApp";
const LWC_COMPONENT_TYPE = "c:FlowContainer";
const LWC_LOCATOR = "lightning-web-component";
const LWC_HOST_DEV = "https://emory--casedev.sandbox.my.site.com/Finance";
const LWC_HOST_QA = "https://emory--qa.sandbox.my.site.com/Finance";
const LWC_HOST_PROD = "https://emory.my.site.com/FinanceSupport";
const LWC_LIGHTNING_OUT_SCRIPT_URL_PATH = "/lightning/lightning.out.js";
const SHOW_SIDEBAR = false;

const EUFinance: FC = (): ReactElement => {
  const { data: currentUser, isLoading: isLoadingCurrentUser } =
    useEUFinanceCurrentUser();
  const [isLoadingLWC, setIsLoadingLWC] = useState<boolean>(true);
  const [scriptLoaded, setScriptLoaded] = useState<boolean>(false);

  const lwcHost = useMemo(() => {
    const { hostname } = window.location;

    if (hostname.startsWith("dev-") || hostname.startsWith("localhost")) {
      return LWC_HOST_DEV;
    }

    if (hostname.startsWith("qa-")) {
      return LWC_HOST_QA;
    }

    return LWC_HOST_PROD;
  }, []);

  useEffect(() => {
    if (lwcHost) {
      const script = document.createElement("script");
      script.src = `${lwcHost}${LWC_LIGHTNING_OUT_SCRIPT_URL_PATH}`;
      script.async = true;
      script.onload = () => setScriptLoaded(true);
      document.body.appendChild(script);
    }
  }, [lwcHost]);

  useEffect(() => {
    if (lwcHost && scriptLoaded && currentUser?.id) {
      const lightning = (window as unknown as HasLightning).$Lightning;
      if (lightning) {
        lightning.use(
          LWC_APPLICATION_TAG,
          () => {
            lightning.createComponent(
              LWC_COMPONENT_TYPE,
              { contactId: currentUser?.id },
              LWC_LOCATOR,
              () => {
                setIsLoadingLWC(false);
              }
            );
          },
          lwcHost
        );
      }
    }
  }, [currentUser?.id, lwcHost, scriptLoaded]);

  if (isLoadingCurrentUser) {
    return (
      <div className="eu-finance__loading d-flex justify-content-center align-items-center">
        <FontAwesomeIcon icon={faSpinner} spin size="10x" />
      </div>
    );
  }

  if (!currentUser?.id) {
    return (
      <div className="eu-finance__error">
        <Container fluid>
          <Row>
            <Col className="mt-3">
              <Alert variant="danger">
                Oops! We could not locate your account. Try signing in again or
                contact support if the issue continues.
              </Alert>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="eu-finance">
      <Container fluid>
        <Row>
          {SHOW_SIDEBAR && (
            <Col xs={2} className="sidebar-wrapper">
              <Nav className="d-block bg-light sidebar col-2">
                <Nav.Link className="pe-none">
                  <h4>Log a Ticket</h4>
                </Nav.Link>
                {[
                  "Accounts Payable / Procurement",
                  "Cash Application",
                  "Compass",
                  "EBI (Emory Business Intelligence) / Reporting",
                  "FSC: Pre-award Activities",
                  "General AR & Billing",
                ].map((category) => (
                  <Nav.Link key={category}>{category}</Nav.Link>
                ))}
              </Nav>
            </Col>
          )}
          <Col xs={SHOW_SIDEBAR ? 10 : 12} className="page-content-wrapper">
            {isLoadingLWC && (
              <div className="page-content-wrapper__spinner-wrapper d-flex justify-content-center align-items-center">
                <FontAwesomeIcon icon={faSpinner} spin size="10x" />
              </div>
            )}
            <div className={isLoadingLWC ? "d-none" : undefined}>
              <div id={LWC_LOCATOR} />
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EUFinance;
